import { supabase } from "@/integrations/supabase/client";

/**
 * "Husk mig" (Remember me) — keeps the user signed in for 30 days across browser
 * restarts. When disabled, the session is dropped as soon as the browser session
 * ends (new tab/window launch without the in-memory session marker).
 *
 * Works for both password sign-in and Google OAuth: the preference is written
 * before the auth call, so it survives the OAuth redirect.
 */
const REMEMBER_KEY = "ck_remember_me";
const EXPIRES_KEY = "ck_remember_expires_at";
const SESSION_MARKER = "ck_session_active";

export const REMEMBER_ME_DAYS = 30;
const REMEMBER_ME_MS = REMEMBER_ME_DAYS * 24 * 60 * 60 * 1000;

function safeLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Persist the user's choice. Call this BEFORE signIn / signUp / OAuth redirect. */
export function setRememberMe(remember: boolean) {
  const ls = safeLocal();
  const ss = safeSession();
  ls?.setItem(REMEMBER_KEY, remember ? "1" : "0");
  if (remember) {
    ls?.setItem(EXPIRES_KEY, String(Date.now() + REMEMBER_ME_MS));
  } else {
    ls?.removeItem(EXPIRES_KEY);
  }
  // Mark this browser session as active so a non-remembered session survives
  // in-tab navigation and reloads.
  ss?.setItem(SESSION_MARKER, "1");
}

export function getRememberMe(): boolean {
  // Default to remembering (previous behaviour) when nothing was ever stored.
  return safeLocal()?.getItem(REMEMBER_KEY) !== "0";
}

/** Slide the 30-day window forward while the user keeps using the app. */
export function refreshRememberWindow() {
  if (!getRememberMe()) return;
  safeLocal()?.setItem(EXPIRES_KEY, String(Date.now() + REMEMBER_ME_MS));
}

export function clearRememberMe() {
  const ls = safeLocal();
  ls?.removeItem(REMEMBER_KEY);
  ls?.removeItem(EXPIRES_KEY);
  safeSession()?.removeItem(SESSION_MARKER);
}

/**
 * Enforce the policy on app boot. Signs the user out when:
 *  - remember me was off and this is a brand-new browser session, or
 *  - the 30-day remember window has expired.
 */
export async function enforceRememberMePolicy(): Promise<void> {
  const ls = safeLocal();
  const ss = safeSession();
  if (!ls || !ss) return;

  const { data } = await supabase.auth.getSession();
  const hasSession = Boolean(data.session);
  const sessionActive = ss.getItem(SESSION_MARKER) === "1";

  if (!hasSession) {
    if (sessionActive) ss.removeItem(SESSION_MARKER);
    return;
  }

  const remember = getRememberMe();

  if (!remember) {
    if (!sessionActive) {
      await supabase.auth.signOut();
      return;
    }
    return;
  }

  const expiresAt = Number(ls.getItem(EXPIRES_KEY) || 0);
  if (expiresAt && Date.now() > expiresAt) {
    await supabase.auth.signOut();
    clearRememberMe();
    return;
  }

  // Keep the marker + sliding window fresh.
  ss.setItem(SESSION_MARKER, "1");
  refreshRememberWindow();
}
